import 'react-data-grid/lib/styles.css';

import { type FC, useEffect, useState } from 'react';
import DataGrid, { type ColumnOrColumnGroup } from 'react-data-grid';
import * as XLSX from 'xlsx';

import { Button, Loading } from '@zen/DesignSystem';

type Props = {
  fileUrl: string;
  height?: number;
  width?: number;
};

type Sheet = {
  columns: ColumnOrColumnGroup<unknown, unknown>[];
  name: string;
  rows: unknown[];
};

const XlsxViewer: FC<Props> = ({ fileUrl, height = 650, width }) => {
  const [sheets, setSheets] = useState<Sheet[]>([]);
  const [activeSheet, setActiveSheet] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAndParseXlsx = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(fileUrl);

        if (!response.ok) {
          throw new Error('Failed to fetch file');
        }

        const arrayBuffer = await response.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });

        const parsedSheets = workbook.SheetNames.map((name) => {
          const worksheet = workbook.Sheets[name];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

          const columns =
            (jsonData[0] as string[])?.map((header, index) => ({
              key: header || `Column${index + 1}`,
              name: header || `Column${index + 1}`,
              resizable: true,
              sortable: true,
              width: 150
            })) || [];

          const rows = jsonData.slice(1).map((row: unknown) => {
            const rowData: Record<string, unknown> = {};

            columns.forEach((col, index) => {
              rowData[col.key] = (row as unknown[])[index];
            });

            return rowData;
          });

          return {
            name,
            columns,
            rows
          };
        });

        setSheets(parsedSheets);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load file');
      } finally {
        setLoading(false);
      }
    };

    fetchAndParseXlsx();
  }, [fileUrl]);

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return <div>Failed to load file</div>;
  }

  if (sheets.length === 0) {
    return <div>No data available</div>;
  }

  const activeSheetData = sheets[activeSheet];

  return (
    <div style={{ height, width }}>
      {sheets.length > 1 && (
        <div className="flex space-x-4 mb-4">
          {sheets.map((sheet, index) => (
            <Button key={sheet.name} onClick={() => setActiveSheet(index)} size="compact" variant="secondary">
              {sheet.name}
            </Button>
          ))}
        </div>
      )}
      <DataGrid
        className="rdg-light"
        columns={activeSheetData.columns}
        rowHeight={35}
        rows={activeSheetData.rows}
        style={{ height: sheets.length > 1 ? height - 50 : height }}
      />
    </div>
  );
};

export default XlsxViewer;
